<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();
useHead({
  titleTemplate: () => t("seo.layouts./viber-bot-verification.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-bot-verification.description") },
    {
      property: "og:title", content: () => t("seo.layouts./viber-bot-verification.title")
    },
    { property: "og:description", content: () => t("seo.layouts./viber-bot-verification.description") },
    {
      property: "og:image",
      content: "/images/chatbot.png",
    },
    { property: "og:image:width", content: "2668" },
    { property: "og:image:height", content: "2000" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
});

const REGISTER_URL = 'https://omni.smsbat.com/auth/login?regView=true';
const WHOP_CHECKOUT_URL = 'https://whop.com/checkout/plan_JeI6Ns18NMTUL';

const showPaymentModal = ref(false);

const payWithWhop = () => {
  window.open(WHOP_CHECKOUT_URL, '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
};

const payWithInvoice = () => {
  window.open('viber://chat?service=26076', '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
};

const popularQuestions = ref([
  {
    question: t('botVerificationLanding.faq[0].question'),
    answer: t('botVerificationLanding.faq[0].answer'),
    isOpen: false
  },
  {
    question: t('botVerificationLanding.faq[1].question'),
    answer: t('botVerificationLanding.faq[1].answer'),
    isOpen: false
  },
  {
    question: t('botVerificationLanding.faq[2].question'),
    answer: t('botVerificationLanding.faq[2].answer'),
    isOpen: false
  },
  {
    question: t('botVerificationLanding.faq[3].question'),
    answer: t('botVerificationLanding.faq[3].answer'),
    isOpen: false
  },
])

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})
</script>

<template>
  <div>
    <!-- Hero Banner -->
    <div class="main-banner">
      <div class="main-banner-content !justify-center gap-8">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('botVerificationLanding.hero.title') }}
          </div>
          <div class="banner-description">
            {{ $t('botVerificationLanding.hero.subtitle') }}
          </div>
          <div class="banner-action">
            <NuxtLink :to="REGISTER_URL" external target="_blank" class="button filled whitespace-nowrap !text-sm">
              <p>{{ $t('botVerificationLanding.hero.cta') }}</p>
            </NuxtLink>
            <button @click="showPaymentModal = true" class="button outlined whitespace-nowrap !text-sm">
              {{ $t('botVerificationLanding.payment.title') }}
            </button>
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg preload sizes="xs:100vw sm:100vw md:50vw lg:600px xl:800px xxl:1000px" src="/images/chatbot.png" :alt="$t('seo.layouts./viber-bot-verification.title')" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" class="w-full h-auto object-contain" />
        </div>
      </div>
    </div>

    <!-- Process Steps Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('botVerificationLanding.process.title') }}</div>
          <p class="mt-4 text-gray-600">{{ $t('botVerificationLanding.process.subtitle') }}</p>
        </div>
        <div class="section-content">
          <div class="process-steps">
            <div v-for="(step, index) in 5" :key="index" class="process-step">
              <div class="step-number">{{ index + 1 }}</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t(`botVerificationLanding.process.steps[${index}].title`) }}</h3>
                <p class="step-description">{{ $t(`botVerificationLanding.process.steps[${index}].description`) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section service-domain" id="pricing">
      <div class="container">
        <div class="service-price">
          <div class="price-label">{{ $t('global.price') }}</div>
        </div>
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('botVerificationLanding.pricing.title') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-cards">
            <!-- Verification Fee -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>{{ $t('botVerificationLanding.pricing.verification.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">50 EUR</span>
                  <span class="price-note">{{ $t('botVerificationLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('botVerificationLanding.pricing.verification.description') }}</p>
              </div>
            </div>

            <!-- Deposit -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>{{ $t('botVerificationLanding.pricing.deposit.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">300 EUR</span>
                  <span class="price-note">{{ $t('botVerificationLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('botVerificationLanding.pricing.deposit.description') }}</p>
              </div>
            </div>
          </div>

          <!-- What's Included -->
          <div class="traffic-info">
            <div class="traffic-info-content">
              <span class="pi pi-info-circle text-2xl text-blue-700"></span>
              <div>
                <h4>{{ $t('botVerificationLanding.pricing.included.title') }}</h4>
                <p>{{ $t('botVerificationLanding.pricing.included.description') }}</p>
              </div>
            </div>
          </div>

          <!-- Total to Start -->
          <div class="total-start">
            <div class="total-start-content">
              <h3>{{ $t('botVerificationLanding.pricing.total.title') }}</h3>
              <div class="total-amount">
                <span class="total-price">350 EUR</span>
                <span class="total-note">{{ $t('botVerificationLanding.pricing.vatNote') }}</span>
              </div>
              <p class="total-breakdown">{{ $t('botVerificationLanding.pricing.total.breakdown') }}</p>
              <button @click="showPaymentModal = true" class="button filled whitespace-nowrap mt-4" style="border-color: white; background: white; color: #044068;">
                {{ $t('botVerificationLanding.payment.title') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('botVerificationLanding.features.title') }}</div>
        </div>
        <div class="section-content">
          <div class="features-grid">
            <div v-for="(item, index) in 4" :key="index" class="feature-card">
              <div class="feature-icon">
                <span :class="['pi text-3xl', index === 0 ? 'pi-cog' : index === 1 ? 'pi-lock-open' : index === 2 ? 'pi-envelope' : 'pi-headphones']"></span>
              </div>
              <h3>{{ $t(`botVerificationLanding.features.items[${index}].title`) }}</h3>
              <p>{{ $t(`botVerificationLanding.features.items[${index}].description`) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section" id="registration">
      <div class="container">
        <div class="cta-section">
          <h2>{{ $t('botVerificationLanding.hero.cta') }}</h2>
          <p class="cta-description">{{ $t('botVerificationLanding.hero.subtitle') }}</p>
          <div class="cta-buttons">
            <NuxtLink :to="REGISTER_URL" external target="_blank" class="button filled whitespace-nowrap">
              <p>{{ $t('botVerificationLanding.hero.cta') }}</p>
            </NuxtLink>
            <button @click="showPaymentModal = true" class="button outlined whitespace-nowrap">
              {{ $t('botVerificationLanding.payment.title') }}
            </button>
            <NuxtLink to="viber://chat?service=26076" class="button outlined" external>
              {{ $t('botVerificationLanding.hero.contactUs') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="page-section">
      <div class="container">
        <ClientOnly>
          <PopularQuestions :title="$t('botVerificationLanding.faq.title')" :questions="popularQuestions" />
        </ClientOnly>
      </div>
    </div>

    <!-- Payment Modal -->
    <Dialog v-model:visible="showPaymentModal" modal :header="$t('botVerificationLanding.payment.title')" :style="{ width: '500px' }" :closable="true">
      <div class="payment-modal-content">
        <p class="payment-description">{{ $t('botVerificationLanding.payment.description') }}</p>

        <div class="payment-amount">
          <span class="amount">350 EUR</span>
          <span class="note">{{ $t('botVerificationLanding.pricing.vatNote') }}</span>
        </div>

        <div class="payment-options">
          <button @click="payWithWhop" class="payment-btn whop">
            <span class="pi pi-credit-card"></span>
            <div>
              <strong>{{ $t('botVerificationLanding.payment.whop.title') }}</strong>
              <small>{{ $t('botVerificationLanding.payment.whop.description') }}</small>
            </div>
          </button>

          <button @click="payWithInvoice" class="payment-btn invoice">
            <span class="pi pi-building"></span>
            <div>
              <strong>{{ $t('botVerificationLanding.payment.swift.title') }}</strong>
              <small>{{ $t('botVerificationLanding.payment.swift.description') }}</small>
            </div>
          </button>
        </div>

        <p class="payment-note">{{ $t('botVerificationLanding.payment.note') }}</p>
      </div>
    </Dialog>
  </div>
</template>

<style lang="scss" scoped>
.banner-image-container {
  width: 100%;
  margin-top: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
}

@media (max-width: 900px) {
  .banner-image-container {
    margin-top: 50px;
  }
}

@media (max-width: 850px) {
  .main-banner-content {
    display: block;
  }
}

.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}

/* Process Steps */
.process-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.process-step {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.process-step:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.step-number {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.step-description {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Pricing Cards */
.pricing-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.pricing-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  transition: transform 0.2s, box-shadow 0.2s;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.pricing-card-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.pricing-card-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.pricing-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.price-note {
  font-size: 0.75rem;
  color: #9ca3af;
}

.pricing-card-body {
  text-align: center;
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Traffic Info */
.traffic-info {
  margin-top: 2rem;
  background: #eff6ff;
  border-radius: 12px;
  padding: 1.5rem;
}

.traffic-info-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.traffic-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.traffic-info p {
  font-size: 0.95rem;
  color: #6b7280;
}

/* Total to Start */
.total-start {
  margin-top: 2rem;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
}

.total-start-content {
  text-align: center;
}

.total-start h3 {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.total-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.total-price {
  font-size: 3rem;
  font-weight: 700;
}

.total-note {
  font-size: 0.85rem;
  opacity: 0.8;
}

.total-breakdown {
  font-size: 0.95rem;
  opacity: 0.9;
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.feature-card {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.feature-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.feature-card h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.feature-card p {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

/* CTA Section */
.cta-section {
  text-align: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  border-radius: 24px;
  color: white;
}

.cta-section h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.cta-description {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-buttons .button.filled {
  background: white;
  color: #7c3aed;
}

.cta-buttons .button.outlined {
  border-color: white;
  color: white;
  background: transparent;
}

/* Form Section */
#registration {
  background: #f9fafb;
}
</style>

<style>
.payment-modal-content {
  text-align: center;
}

.payment-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
}

.payment-amount {
  background: linear-gradient(135deg, #3070b6, #044068);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.payment-amount .amount {
  font-size: 2.5rem;
  font-weight: 700;
  display: block;
}

.payment-amount .note {
  font-size: 0.85rem;
  opacity: 0.8;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.payment-modal-content .payment-btn {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 1rem !important;
  padding: 1.25rem !important;
  border-radius: 12px !important;
  border: 2px solid #e5e7eb !important;
  background: white !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  text-align: left !important;
  width: 100% !important;
}

.payment-modal-content .payment-btn:hover {
  border-color: #3070b6 !important;
  background: #eff6ff !important;
}

.payment-modal-content .payment-btn .pi {
  font-size: 1.5rem !important;
  color: #3070b6 !important;
  flex-shrink: 0 !important;
}

.payment-modal-content .payment-btn > div {
  flex: 1 !important;
  text-align: left !important;
}

.payment-modal-content .payment-btn strong {
  display: block !important;
  color: #1f2937 !important;
  margin-bottom: 0.25rem !important;
  font-size: 1rem !important;
}

.payment-modal-content .payment-btn small {
  display: block !important;
  color: #6b7280 !important;
  font-size: 0.85rem !important;
}

.payment-modal-content .payment-btn.invoice:hover {
  border-color: #0284c7 !important;
}

.payment-modal-content .payment-btn.invoice .pi {
  color: #0284c7 !important;
}

.payment-note {
  font-size: 0.85rem;
  color: #9ca3af;
  font-style: italic;
}
</style>
